/**
 * Network Status Service
 * Monitors network connectivity using Capacitor Network plugin
 * Falls back to navigator.onLine on web platform
 */

import CapacitorService, { NetworkStatus } from './capacitor';
import { LoggingService } from './logging';

export class NetworkService {
  private static listener: { remove: () => void } | null = null;
  private static callbacks: Array<(status: NetworkStatus) => void> = [];

  /**
   * Initialize network status monitoring
   */
  static async init(): Promise<void> {
    // Add listener for network status changes
    this.listener = await CapacitorService.network.addListener((status) => {
      LoggingService.Log('Network status changed: ' + status);

      // Notify all registered callbacks
      this.callbacks.forEach((callback) => callback(status));
    });

    // Get initial status
    const status = await this.getStatus();
    LoggingService.Log('Initial network status: ' + status);
  }

  /**
   * Get current network status
   */
  static async getStatus(): Promise<NetworkStatus> {
    return await CapacitorService.network.getStatus();
  }

  /**
   * Check if currently connected to network
   */
  static async isConnected(): Promise<boolean> {
    const status = await this.getStatus();
    return status.connected;
  }

  /**
   * Register a callback for network status changes
   */
  static addStatusChangeListener(callback: (status: NetworkStatus) => void): void {
    this.callbacks.push(callback);
  }

  /**
   * Remove a callback
   */
  static removeStatusChangeListener(callback: (status: NetworkStatus) => void): void {
    const index = this.callbacks.indexOf(callback);
    if (index > -1) {
      this.callbacks.splice(index, 1);
    }
  }

  /**
   * Cleanup - call when app is destroyed
   */
  static cleanup(): void {
    if (this.listener) {
      this.listener.remove();
      this.listener = null;
    }
    this.callbacks = [];
  }
}

export default NetworkService;
